import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, Warning, TrendUp, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const SEV_COLORS = {
  critical: "border-l-[#D0021B] bg-red-50",
  high: "border-l-orange-500 bg-orange-50",
  medium: "border-l-amber-500 bg-amber-50",
  low: "border-l-zinc-400 bg-zinc-50",
};

const SUGGESTED = [
  "What's our biggest financial risk this quarter?",
  "Which department is most expensive and is the spend justified?",
  "How healthy is our inventory? Anything I should reorder now?",
  "Give me a one-paragraph executive summary of operations today.",
];

export default function AnalyticsPage() {
  const [alerts, setAlerts] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [a, f] = await Promise.all([
        api.get("/analytics/alerts").then((r) => r.data),
        api.get("/analytics/forecast").then((r) => r.data),
      ]);
      setAlerts(a); setForecast(f);
    } catch (e) {
      if (e.response?.status === 402) toast.error("Analytics module requires Enterprise plan or higher.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (!query) return;
    setQuestion(query); setBusy(true); setAnswer("");
    try {
      const { data } = await api.post("/analytics/ask", { question: query });
      setAnswer(data.answer || "(no response)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="analytics-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 08 · ANALYTICS</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Insights.</h1>
          <p className="text-sm text-zinc-500 mt-2">Rule-based alerts, cash-flow forecast, and natural-language reporting.</p>
        </div>
        <button onClick={refresh} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1">
          <ArrowsClockwise size={12} /> REFRESH
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 mb-8">
        {/* Alerts */}
        <div className="bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-sm text-zinc-500">Active Alerts</div>
            <span className="font-mono text-[10px] text-zinc-400">{alerts.length} TOTAL</span>
          </div>
          <div className="space-y-2" data-testid="alerts-list">
            {alerts.length === 0 && <div className="text-xs text-zinc-400 font-mono">NO ACTIVE ALERTS</div>}
            {alerts.map((a, i) => (
              <div key={`${a.module}-${a.title}-${i}`} className={`border-l-4 px-3 py-2 ${SEV_COLORS[a.severity] || "border-l-zinc-300 bg-white"}`} data-testid={`alert-${i}`}>
                <div className="flex items-center gap-2">
                  <Warning size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{a.module}</span>
                  <span className="text-[10px] font-mono ml-auto">{a.severity?.toUpperCase()}</span>
                </div>
                <div className="text-sm font-semibold mt-0.5">{a.title}</div>
                <div className="text-xs text-zinc-600">{a.detail}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Forecast */}
        <div className="bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-sm text-zinc-500">90-Day Cash Flow Forecast</div>
            <TrendUp size={14} className="text-zinc-400" />
          </div>
          {forecast && (
            <div data-testid="forecast-list">
              <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-3">
                <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2">Period</div>
                <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">In</div>
                <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Out</div>
                <div className="bg-zinc-50 label-sm text-zinc-500 px-3 py-2 text-right">Net</div>
                {forecast.months.map((m) => (
                  <div key={m.period} className="contents">
                    <div className="bg-white px-3 py-2 text-sm font-medium">{m.period}</div>
                    <div className="bg-white px-3 py-2 text-sm text-right tnum text-emerald-700">{fmt$(m.cash_in)}</div>
                    <div className="bg-white px-3 py-2 text-sm text-right tnum text-[#D0021B]">{fmt$(m.cash_out)}</div>
                    <div className={`bg-white px-3 py-2 text-sm text-right tnum font-semibold ${m.net >= 0 ? "text-emerald-700" : "text-[#D0021B]"}`}>{fmt$(m.net)}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-zinc-500">
                Total net: <span className={`tnum font-semibold ${forecast.total_net >= 0 ? "text-emerald-700" : "text-[#D0021B]"}`}>{fmt$(forecast.total_net)}</span>
              </div>
              <details className="mt-3">
                <summary className="font-mono text-[10px] text-zinc-400 cursor-pointer hover:text-zinc-700">ASSUMPTIONS</summary>
                <ul className="text-xs text-zinc-500 mt-2 pl-4 list-disc">
                  {forecast.assumptions.map((a) => <li key={a}>{a}</li>)}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* NL reporting */}
      <div className="border border-zinc-950">
        <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
          <Sparkle size={12} weight="fill" /> natural_language.reporting
        </div>
        <div className="p-6">
          <p className="text-sm text-zinc-600 mb-4">Ask any question about your business in plain English. Powered by Claude with full ERP context.</p>

          <div className="flex flex-wrap gap-2 mb-4">
            {SUGGESTED.map((s, i) => (
              <button key={s} onClick={() => ask(s)} disabled={busy} className="text-xs border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-50 px-3 py-1.5 transition-colors disabled:opacity-40" data-testid={`analytics-suggest-${i}`}>
                {s}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 border border-zinc-300 focus-within:border-zinc-950 px-3 py-2">
            <Sparkle size={14} className="text-[#FF3E00]" weight="fill" />
            <input
              data-testid="analytics-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
              placeholder="Ask anything…"
              className="flex-1 text-sm bg-transparent outline-none"
              disabled={busy}
            />
            <button onClick={() => ask()} disabled={busy || !question.trim()} className="text-xs font-semibold text-zinc-950 disabled:opacity-40" data-testid="analytics-ask-btn">
              {busy ? "ANALYZING…" : "ASK"}
            </button>
          </div>

          {(answer || busy) && (
            <div className="mt-5 border-l-2 border-[#FF3E00] pl-4 py-2" data-testid="analytics-answer">
              {busy && <div className="font-mono text-xs text-zinc-400 pulse-dot">Generating insight…</div>}
              {answer && <div className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-950">{answer}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
